import { forwardRef, useState, type ReactNode } from "react";
import { Popover as PopoverPrimitive } from "radix-ui";

import { useDialogPortalContainer } from "@/components/ui/dialog-portal-container";
import {
  MUI_FIELD_TRIGGER,
  MUI_POPOVER_MARGIN_THRESHOLD,
  MUI_SELECT_MENU_PANEL,
} from "@/components/ui/mui-field-shape";
import { OutlinedField } from "@/components/ui/outlined-field";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import {
  MUI_SELECT_CARET_ANCHOR,
  MUI_SELECT_CARET_DISABLED,
  MUI_SELECT_CARET_GUTTER,
  MuiSelectCaret,
} from "@/components/ui/mui-select-caret";
import { cn } from "@/lib/utils";

// The parts the two ported catalog selects are both built from.
//
// The donor draws both with MUI's outlined `Select` and a `Menu` that carries a
// pinned search box (`CustomSelectWithPagination`, `CategoriesFieldPaginated`),
// and the theme overrides neither — so MUI's defaults are the spec. The values
// that ride here: a menu no taller than 500px, opening 8px below a trigger it
// matches the width of; rows at least 48px tall with 16px of horizontal padding
// running edge to edge; the current row marked with a tint rather than a tick;
// and the search box sticky at the top on the menu's own background.
//
// **A popover, not `@/components/ui/select`.** Radix's Select owns keyboard
// focus for typeahead and cannot hold a text input, and a search box inside the
// menu is the donor behavior both these controls are built around. The trigger
// therefore carries the combobox role and state by hand.

/**
 * A whole ported select: the popover, the trigger inside it, and the menu.
 *
 * Both controls opened with the identical `Popover` / `PopoverTrigger asChild` /
 * wrapper-`div` / `MenuSelectTrigger` block — including the `div`, which is
 * there because `PopoverTrigger asChild` clones props onto its child and
 * `MenuSelectTrigger` renders a frame, not a bare button. One copy, so the two
 * cannot drift on which of them is the combobox.
 */
export function MenuSelect({
  open,
  onOpenChange,
  trigger,
  children,
  className,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: ReactNode;
  children: ReactNode;
  /**
   * Classes on the wrapper, which is the control's box in its parent's layout
   * — MUI's `FormControl` root, where a donor's `fullWidth` lands.
   */
  className?: string;
}) {
  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <div className={className}>{trigger}</div>
      </PopoverTrigger>
      {children}
    </Popover>
  );
}

/**
 * The vendored `PopoverContent`'s own chrome, verbatim.
 *
 * Spelled out here because `MenuPopoverContent` below has to own the *portal*,
 * and the vendored component keeps its portal private. Everything else about
 * the look still comes from that file — copied, not re-decided, so the two do
 * not drift. The panel constants layer over it and win through `cn`.
 */
const POPOVER_CHROME =
  "bg-popover text-popover-foreground data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 z-50 w-72 origin-(--radix-popover-content-transform-origin) rounded-md border p-4 shadow-md outline-hidden";

/**
 * A popover panel that lands **inside the dialog it was opened from**.
 *
 * The vendored `PopoverContent` always portals to `document.body`. That is
 * right on a page and wrong inside a modal dialog: Radix locks scroll with
 * `react-remove-scroll`, which cancels every wheel event whose target is not
 * inside the dialog's content element, so a body-portalled menu is scrollable
 * by keyboard and by dragging its bar and **dead under a trackpad**. Portalling
 * into the dialog's own node puts the menu inside the lock, where the wheel
 * reaches it.
 *
 * Outside a dialog `useDialogPortalContainer()` is `null` and Radix falls back
 * to the body portal — the existing behavior, unchanged.
 *
 * Shared rather than local to one control because every popover-backed select
 * a dialog hosts has the same bug: this file's menu, and `tree-select`'s.
 */
export const MenuPopoverContent = forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof PopoverPrimitive.Content>
>(function MenuPopoverContent({ className, onEscapeKeyDown, ...props }, ref) {
  const container = useDialogPortalContainer();

  return (
    <PopoverPrimitive.Portal container={container ?? undefined}>
      <PopoverPrimitive.Content
        ref={ref}
        data-slot="popover-content"
        // MUI's `marginThreshold`. Ahead of `{...props}`, so a call site that
        // needs a different margin can still say so.
        collisionPadding={MUI_POPOVER_MARGIN_THRESHOLD}
        // **Escape typed into one of the menu's own fields does nothing.** Every
        // text field a donor menu holds swallows its keys before they leave —
        // the search box (`search-input.tsx:38`) and both inline lookup forms
        // (`AddOptionButton.tsx:67`, `EditableOption.tsx:77`) — which is how a
        // menu built out of `MenuItem`s stops typeahead eating what is being
        // typed, and it takes Escape with it. Escape from the trigger still
        // closes, so the keyboard is not shut in.
        onEscapeKeyDown={(event) => {
          const active = document.activeElement;
          if (
            active instanceof HTMLInputElement &&
            active.closest("[data-slot='popover-content']")
          ) {
            event.preventDefault();
          }
          onEscapeKeyDown?.(event);
        }}
        className={cn(POPOVER_CHROME, className)}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
});

/** The menu surface: donor sizing, and the trigger's own width. */
export const MenuSelectContent = forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof MenuPopoverContent>
>(function MenuSelectContent({ className, children, ...props }, ref) {
  return (
    <MenuPopoverContent
      ref={ref}
      align="start"
      sideOffset={8}
      // **A `Select`'s menu, not an `Autocomplete`'s.** The donor passes no
      // width — only `maxHeight: 500` and `mt: 1`
      // (`CustomSelectWithPagination.tsx:215-226`) — so MUI takes the trigger's
      // width as a *minimum* and grows the paper to its longest row. Clamped to
      // the trigger instead, a long manufacturer name ellipsised in a field
      // that is only a third of the form wide.
      className={cn(MUI_SELECT_MENU_PANEL, className)}
      {...props}
    >
      {/* MUI's `MenuList`, **with its padding zeroed** — the donor overrides
          exactly this for exactly this control, `slotProps={{ list: { sx: { p:
          0 } } }}` (`CustomSelectWithPagination.tsx:221-225`), so the first
          row sits against the search box and the last against the add row
          rather than inside MUI's default 8px band. Carrying `MUI_MENU_LIST`
          here put that band back: measured in #412's pixel review, ruled in
          its decision round (D8 = a, 2026-09-15). It is zeroed *here* and not
          on the token, because the token is right everywhere else it is used —
          `tree-select.tsx` and `multi-select-field.tsx` are MUI menus whose
          donors do not override the padding, and this surface belongs to the
          three lookup menus alone.

          `role="none"` keeps the wrapper out of the listbox's accessibility
          tree, so the options are still the listbox's own children to a
          screen reader. */}
      <div role="none">{children}</div>
    </MenuPopoverContent>
  );
});

interface MenuSelectTriggerProps {
  label: string;
  /** What the trigger reads when something is chosen; blank when nothing is. */
  display: string;
  open: boolean;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  helperText?: string;
  triggerId: string;
  /** The label element's id, for the trigger's `aria-labelledby`. */
  labelId: string;
  messageId?: string;
  className?: string;
  "data-testid"?: string;
  /**
   * MUI's `variant`. `outlined` — the frame and its floated label — unless a
   * donor says otherwise; `standard` is the Spec list's division selector
   * (`division-select-island.tsx:64-78`), which also passes `disableUnderline`,
   * so it is drawn with no line at all.
   */
  variant?: "outlined" | "standard";
  /**
   * MUI's `size` on the outlined frame: `medium`, the 56px default, unless the
   * donor passes `small` — the per-plan manufacturer dialog's
   * `size='small'` (`PerPlanManufacturerDialog.tsx:94`), 40px. See
   * `OutlinedField`'s prop.
   */
  size?: "small" | "medium";
  /** Classes on the chosen value — a donor `sx` reaching `.MuiInputBase-input`. */
  valueClassName?: string;
}

/**
 * The closed control: the outlined frame, the chosen value, and the arrow.
 *
 * MUI flips its arrow when the menu opens, so the rotation is a transition on
 * the open state rather than a second icon.
 */
export function MenuSelectTrigger({
  label,
  display,
  open,
  required = false,
  disabled = false,
  error,
  helperText,
  triggerId,
  labelId,
  messageId,
  className,
  "data-testid": testId,
  variant = "outlined",
  size = "medium",
  valueClassName,
}: MenuSelectTriggerProps) {
  const message = error ?? helperText;
  // `FormControl`'s own focused state, which the label's `shrink` reads.
  const [hasFocus, setHasFocus] = useState(false);

  if (variant === "standard") {
    return (
      // A standard `Input` with `disableUnderline`: no frame, no label, and
      // nothing drawn under it. The donor's own `mt: 0.5` sits on this root
      // (`division-select-island.tsx:73`).
      <div className={cn("relative flex w-full items-center", className)}>
        {/* **Named by a hidden label and the value**, as the donor's
            `SelectField` names it (`SelectField.tsx:23-34`): MUI 9 no longer
            folds either into the combobox's name, so the donor points
            `aria-labelledby` at a visually hidden span and at the display
            itself — "Division dropdown View All". */}
        <span id={labelId} className="sr-only">
          {label}
        </span>
        <button
          id={triggerId}
          type="button"
          role="combobox"
          aria-labelledby={`${labelId} ${triggerId}`}
          data-testid={testId}
          aria-expanded={open}
          aria-haspopup="listbox"
          disabled={disabled}
          className={cn(
            // `InputBase` at `size='small'`: 16px on a 1.4375em line, `4px 0
            // 5px` less the small step's 3px on top (`InputBase.js:150-178`);
            // the display's `min-height: 1.4375em` and nowrap ellipsis, and the
            // standard variant's 24px gutter for a caret at `right: 0`
            // (`NativeSelectInput.js:62-73,117-121`). No focus wash: MUI 9
            // resets only the radius on `:focus`.
            "text-lennar-ink disabled:text-lennar-ink-disabled flex w-full cursor-pointer items-center pt-px pr-6 pb-[5px] text-left text-base leading-[1.4375em] outline-none disabled:cursor-default",
            MUI_SELECT_CARET_ANCHOR,
            MUI_SELECT_CARET_DISABLED,
          )}
        >
          <span className={cn("min-h-[1.4375em] truncate", valueClassName)}>{display}</span>
          <MuiSelectCaret open={open} className="right-0" />
        </button>
      </div>
    );
  }

  return (
    <OutlinedField
      label={label}
      required={required}
      // MUI's `filled || focused` (`InputLabel.js`): a chosen value shrinks the
      // label, and so does the focus — the per-plan manufacturer dialog focuses
      // its empty field on entering, and the donor's label rides up (#810). An
      // *open but empty* select shrinks it too, though the focus has left for
      // the menu, because the menu would otherwise cover a label sitting in the
      // field it is anchored to.
      // `FormControl` drops `focused` once disabled, as `OutlinedField` notes.
      shrink={display !== "" || open || (hasFocus && !disabled)}
      error={error}
      helperText={helperText}
      disabled={disabled}
      // The menu is portalled, so opening it takes the focus out of the frame.
      // MUI's stays `focused` for as long as the menu is open — see the prop.
      focused={open}
      // `labelId`, not `htmlFor` — see `OutlinedField`'s prop: a `<button>`
      // under a `<label for>` gets a second activation path, and the donor's
      // floated label activates nothing.
      labelId={labelId}
      messageId={message ? messageId : undefined}
      size={size}
      className={className}
    >
      <button
        id={triggerId}
        type="button"
        role="combobox"
        aria-labelledby={labelId}
        data-testid={testId}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={message ? messageId : undefined}
        disabled={disabled}
        onFocus={() => setHasFocus(true)}
        onBlur={() => setHasFocus(false)}
        className={cn(
          "text-lennar-ink disabled:text-lennar-ink-disabled flex w-full items-center px-3.5 text-left text-base leading-6 outline-none disabled:cursor-not-allowed",
          // `.MuiSelect-select`'s `16.5px` over a 23px line at `medium`, and
          // `8.5px` at `small` (`OutlinedInput.js`'s `inputSizeSmall`): 56px
          // and 40px, spelled over this trigger's 24px line.
          size === "small" ? "py-2" : "py-4",
          // The caret places itself against this box now, so the trigger is
          // its containing block and owes it the 32px `.MuiSelect-select`
          // reserves. `justify-between` and the flex `gap-2` go with the old
          // in-flow glyph — with nothing left to push apart, they would only
          // stretch the value's own box.
          // `border-0` inside an `OutlinedField` that owns the outline, so no
          // `--mui-field-frame-border` is published here and the gutter's `0px`
          // fallback applies. Why each of the three: the JSDoc on each in
          // `mui-select-caret.tsx`.
          MUI_SELECT_CARET_ANCHOR,
          MUI_SELECT_CARET_GUTTER,
          MUI_SELECT_CARET_DISABLED,
        )}
      >
        {/* No `min-width`: the running donor's `.MuiSelect-select` computes
            `min-width: 0px`, whatever `NativeSelectInput.js:71`'s `minWidth:
            16` says, so an empty select takes no width for its value wherever
            its width is its content's — the per-plan manufacturer dialog's
            shrink-to-fit paper, 512px empty on the running donor (#810). A
            full-width field never notices. */}
        <span className="truncate">{display}</span>
        <MuiSelectCaret open={open} />
      </button>
    </OutlinedField>
  );
}

interface MenuSelectSearchProps {
  value: string;
  onChange: (next: string) => void;
  /**
   * The control's whole accessible name, and the donor's only one.
   *
   * `SearchInput` is rendered with no `placeholder` prop by all three lookup
   * menus, so it takes its own default and carries no `aria-label` of any kind
   * (`search-input.tsx:8,26-43`) — the placeholder *is* the name, identically
   * in every menu. An `aria-label` here overrode it, so the three menus were
   * announced as "Search manufacturers", "Search materials", and — from
   * `Color/Finish` — "Search color/finishs".
   */
  placeholder?: string;
}

/**
 * The search box pinned at the top of the menu.
 *
 * `autoFocus` is the donor's (`SearchInput`); that the focus *stays* here when
 * the menu opens is Atlas's, and owner-ruled (see the prop). Sticky rather than
 * outside the scroll area so the donor's single scrolling surface is preserved.
 */
export function MenuSelectSearch({ value, onChange, placeholder }: MenuSelectSearchProps) {
  return (
    // `sx={{ p: 1.6 }}` — 12.8px, on the sticky white `TextField` root itself
    // (`search-input.tsx:35-42`).
    <div className="bg-popover sticky top-0 z-10 p-[12.8px]">
      {/* No magnifier: the donor's `SearchInput` is a bare outlined `TextField`
          with no `InputProps` and no adornment
          (`components/search-input/search-input.tsx:26-43`), and it is the
          same component behind both this menu and the category tree's. Which
          means it is also `size='small'` at MUI's own 40px and 16px ink — the
          shared `MUI_FIELD_TRIGGER`, and what the category menu's copy of this
          box already used while this one was drawing 14px in a 38px box. */}
      <input
        // **Focus stays here on open — a named parity break, owner-ruled on #576
        // (item E, 2026-09-22).** The donor passes `autoFocus` too, but MUI's
        // `Menu` takes the focus back to the selected row as it opens, so on the
        // running donor this box sits at its resting grey outline and must be
        // clicked before it takes a key. Atlas keeps the focus in the box and is
        // typeable the moment the menu is up; the owner ruled that stays, and
        // the focused blue outline is simply MUI's own rendering of that state.
        // Two things land it here, and either alone would: this prop, and
        // Radix's `onOpenAutoFocus`, which focuses the panel's first tabbable
        // element. Pinned in the browser test ("typeable at once").
        autoFocus
        type="text"
        placeholder={placeholder ?? "Search by name or keyword"}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        // **The donor's search box swallows every key** — `onKeyDown={(e) =>
        // e.stopPropagation()}` (`search-input.tsx:38`), which is how a menu
        // built out of `MenuItem`s stops typeahead stealing what is being
        // typed. Escape included: it neither closes the menu nor leaves the
        // field, and the way out is a click elsewhere.
        onKeyDown={(event) => event.stopPropagation()}
        className={cn(MUI_FIELD_TRIGGER, "w-full")}
      />
    </div>
  );
}

/**
 * A line of copy where the menu's rows would be — "Loading…", "No matches.",
 * "could not be loaded".
 *
 * **The donor renders no such line**, ever: an empty or still-loading menu is
 * the search box, a gap, and the sticky Add row
 * (`CustomSelectWithPagination.tsx:231-257`). These are kept as owner-ruled
 * parity breaks — #254's decision round, D15 = a (2026-09-02) — because an
 * empty list with no explanation reads as a broken control. The shared
 * component exists so all three read alike and so the break is one thing to
 * find, not three.
 */
export function MenuSelectNotice({ children }: { children: ReactNode }) {
  return <p className="text-lennar-ink-secondary px-4 py-3 text-sm">{children}</p>;
}
